import { Typography } from '@mui/material'
import { ChartsReferenceLine, LineChart } from '@mui/x-charts'
import { BarChart } from '@mui/x-charts/BarChart'
import { IReport } from '../../interface/IReport'
import { dataVisColors } from '@mercedes-benz/mui5-theme'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { useTranslation } from 'react-i18next'
import StatisticsProjects from './StatisticsProjects'

const Statistics = () => {
    const report: IReport[] = useSelector((state: any) => state.report?.value)
    const user = useSelector((state: any) => state.user?.value)

    const currentTheme = useSelector((state: any) => state.theme?.value)
    const totalAmounts = [...report.map((item) => (item?.dayInformation?.mserp_calculated > 0 ? item.dayInformation?.mserp_totalpayflextimebalanceseconds : null))]
    const negativeAmountsExists = totalAmounts.some((amount) => amount < 0)
    const addTime = report.map((item) => (item?.dayInformation?.mserp_calculated > 0 ? item.dayInformation?.mserp_totalpayflextimeaddseconds : null))
    const subTime = report.map((item) => (item?.dayInformation?.mserp_calculated > 0 ? item.dayInformation?.mserp_totalpayflextimesubseconds * -1 : null))
    const capacity = report.map((item) => (item?.dayInformation?.mserp_calculated > 0 ? item.dayInformation?.mserp_timeprofileseconds : null))
    const dates = [...report.map((item) => item?.date)]
    const [colorScheme, setColorScheme] = useState<string[]>()
    const [colorExtendScheme, setColorExtendScheme] = useState<string[]>()
    const { t, i18n } = useTranslation()
    const isStudent: boolean = user?.worker?.mserp_employmentcategory === process.env.REACT_APP_USER_STUDENT

    const positivePhases = !isStudent
        ? [
              { data: Array(dates.length).fill(200), area: true, color: 'rgb(205, 145, 139)', showMark: false, disableHighlight: true, label: t('report.redphase') },
              { data: Array(dates.length).fill(160), area: true, color: 'rgb(233, 182, 115)', showMark: false, disableHighlight: true, label: t('report.yellowphase') },
              { data: Array(dates.length).fill(120), area: true, color: 'rgb(204, 242, 204)', showMark: false, disableHighlight: true, label: t('report.greenphase') },
          ]
        : [
              { data: Array(dates.length).fill(80), area: true, color: 'rgb(205, 145, 139)', showMark: false, disableHighlight: true, label: t('report.redphase') },
              { data: Array(dates.length).fill(40), area: true, color: 'rgb(204, 242, 204)', showMark: false, disableHighlight: true, label: t('report.greenphase') },
          ]

    const negativePhases = !isStudent
        ? [
              { data: Array(dates.length).fill(-100), area: true, color: 'rgb(205, 145, 139)', showMark: false, disableHighlight: true, label: t('report.rednegativephase') },
              { data: Array(dates.length).fill(-60), area: true, color: 'rgb(233, 182, 115)', showMark: false, disableHighlight: true, label: t('report.yellownegativephase') },
              { data: Array(dates.length).fill(-40), area: true, color: 'rgb(204, 242, 204)', showMark: false, disableHighlight: true, label: t('report.greennegativephase') },
          ]
        : [
              { data: Array(dates.length).fill(-50), area: true, color: 'rgb(205, 145, 139)', showMark: false, disableHighlight: true, label: t('report.rednegativephase') },
              { data: Array(dates.length).fill(-20), area: true, color: 'rgb(204, 242, 204)', showMark: false, disableHighlight: true, label: t('report.greennegativephase') },
          ]

    useEffect(() => {
        const dataVisScheme = dataVisColors.basic[currentTheme === 'light' ? 'light' : 'dark']
        const dataVisExtendScheme = dataVisColors.extended[currentTheme === 'light' ? 'light' : 'dark']
        setColorScheme(dataVisScheme)
        setColorExtendScheme(dataVisExtendScheme)
    }, [currentTheme])

    return (
        <>
            <>
                <Typography variant="h6">{t('report.monthlyoverview')}</Typography>
                <BarChart
                    xAxis={[
                        {
                            scaleType: 'band',
                            data: dates,
                            label: t('report.date'),
                            valueFormatter: (value: string | number | Date, context: { location: string }) =>
                                context.location === 'tick' ? `${format(value, 'dd.')}` : `${format(value, 'dd.MM.yyyy')} - ${format(value, 'EEEE', { locale: i18n.language === 'de' ? de : enUS })}`,
                        },
                    ]}
                    series={[
                        { data: capacity, stack: 'time', label: t('report.capacity'), color: '#C9C9C9' },
                        { data: addTime, stack: 'time', label: t('report.increasehours'), color: '#008DFC' },
                        { data: subTime, stack: 'time', label: t('report.decreasehours'), color: '#CC215A' },
                    ]}
                    width={1000}
                    height={500}
                    hideLegend={true}
                    colors={colorScheme}
                    grid={{ horizontal: true }}
                >
                    <ChartsReferenceLine y={0} lineStyle={{ stroke: 'black' }} />
                    <ChartsReferenceLine x={50} lineStyle={{ stroke: 'black' }} />
                </BarChart>
                <Typography variant="h6">{t('report.totalamounthistory')}</Typography>
                <LineChart
                    width={1000}
                    height={400}
                    series={[{ data: totalAmounts, label: t('report.totalamount'), connectNulls: false }, ...positivePhases, ...(negativeAmountsExists ? negativePhases : [])]}
                    xAxis={[
                        {
                            scaleType: 'point',
                            data: dates,
                            label: t('report.date'),
                            valueFormatter: (value: string | number | Date, context: { location: string }) =>
                                context.location === 'tick' ? `${format(value, 'dd.')}` : `${format(value, 'dd.MM.yyyy')} - ${format(value, 'EEEE', { locale: i18n.language === 'de' ? de : enUS })}`,
                        },
                    ]}
                    yAxis={[{ min: negativeAmountsExists ? undefined : 0 }]}
                    colors={colorScheme}
                    hideLegend={true}
                    slotProps={{
                        tooltip: {
                            trigger: 'axis',
                            sx: {
                                '.MuiChartsTooltip-row:not(:first-of-type)': {
                                    display: 'none',
                                },
                            },
                        },
                    }}
                    //tooltip={{ trigger: 'axis' }}
                    grid={{ horizontal: true }}
                    axisHighlight={{ x: 'band' }}
                >
                    <ChartsReferenceLine y={0} lineStyle={{ stroke: 'black' }} />
                </LineChart>
            </>
            <StatisticsProjects colorScheme={colorScheme} colorExtendScheme={colorExtendScheme}></StatisticsProjects>
        </>
    )
}

export default Statistics
